import { Card } from '@components/atoms/Card';
import Alert from '@components/atoms/Alert';
import { formatActivityTime, formatForDisplay } from '@utils/formatDate';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import useFormattedCurrency from '@hooks/useFormattedCurrency';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { ActivityHeader } from '../ActivityHeader';
import { FC } from 'react';
import { TaskName } from '../TaskName';
import { useTranslation } from '@hooks/useTranslation';

interface ActivityBudgetProposalCounteredProps {
  activity: any;
  handleOpenTaskDetail?: (taskId: string) => void;
}

const formatDate = (date?: string | Date | null, notAvailable = 'N/A') =>
  date ? formatForDisplay(date) : notAvailable;

export const ActivityBudgetProposalCountered: FC<
  ActivityBudgetProposalCounteredProps
> = ({ activity, handleOpenTaskDetail }) => {
  const tActivityCards = useTranslation('activityCards');
  const originalProposedAmount = activity.budgetProposal?.proposedAmount || null;
  const formattedOriginalProposed = useFormattedCurrency(originalProposedAmount);

  const requestedStart = activity.budgetRequest?.requestedStartDate;
  const requestedEnd = activity.budgetRequest?.requestedEndDate;
  const proposedStart = activity.budgetProposal?.proposedStartDate;
  const proposedEnd = activity.budgetProposal?.proposedEndDate;

  const counteringOrganization = activity.organization?.name || tActivityCards.notAvailable;
  const originalRequesterOrg =
    activity.budgetRequest?.originatorOrganization?.name || tActivityCards.notAvailable;

  const rightLabel = (
    <div className="flex gap-1">
      <SwapHorizIcon fontSize="small" className="text-blue-500" />
      <p className="text-sm text-blue-500">{tActivityCards.budgetProposalCountered.bidProposalCountered}</p>
      {activity.createdAt && (
        <p className="text-sm text-gray-500">
          {`- ${formatActivityTime(activity.createdAt)}`}
        </p>
      )}
    </div>
  );

  return (
    <Card key={activity.activityId} className="w-full">
      <ActivityHeader activity={activity} rightLabel={rightLabel} />

      <div className="flex gap-1 mt-2">
        <TaskName
          task={activity.task}
          taskId={activity.taskId || activity.task?.taskId}
          onOpenTask={handleOpenTaskDetail}
        />
      </div>

      <div className="flex gap-2 w-full">
        <div className="flex flex-col flex-1 gap-2">
          <Alert className="mt-2">
            <div className="flex flex-col gap-1">
              <div className="flex gap-1">
                <p className="text-sm text-neutral-800 font-semibold">
                  {tActivityCards.taskTypeLabel}
                </p>
                {activity.task?.taskType && (
                  <p className="text-sm text-neutral-800">
                    {activity.task.taskType.split('_').join(' ')}
                  </p>
                )}
              </div>
              <div className="flex gap-1">
                <p className="text-sm text-neutral-800 font-semibold">
                  {tActivityCards.projectLabel}
                </p>
                <p className="text-sm text-neutral-800">
                  {activity.project?.name || tActivityCards.notAvailable}
                </p>
              </div>
            </div>
          </Alert>

          <Alert variant="info" className="h-full" border>
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-1">
                <SwapHorizIcon
                  fontSize="small"
                  className="text-blue-500"
                />
                <p className="text-sm text-blue-950 font-semibold">
                  {tActivityCards.budgetProposalCountered.biddProposalCounteredBy.replace(
                    '{org}',
                    counteringOrganization,
                  )}
                </p>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              <p className="text-sm text-neutral-800">
                <span className="font-semibold">
                  {tActivityCards.budgetProposalCountered.originalProposalLabel}
                </span>{' '}
                {formattedOriginalProposed}
              </p>
              <p className="text-sm text-neutral-800">
                <span className="font-semibold">{tActivityCards.timelineLabel}</span>{' '}
                {formatDate(proposedStart, tActivityCards.notAvailable)} -{' '}
                {formatDate(proposedEnd, tActivityCards.notAvailable)}
              </p>
            </div>
          </Alert>

          <Alert variant="warning" className="h-full" border>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <CreateNewFolderOutlinedIcon
                  fontSize="small"
                  className="text-yellow-950 -mt-1"
                />
                <p className="text-sm text-yellow-950 font-semibold">
                  {tActivityCards.budgetProposalCountered.originalRequestBy.replace(
                    '{org}',
                    originalRequesterOrg,
                  )}
                </p>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              <p className="text-sm text-neutral-800">
                <span className="font-semibold">
                  {tActivityCards.budgetProposalCountered.requestedTimelineLabel}
                </span>{' '}
                {formatDate(requestedStart, tActivityCards.notAvailable)} -{' '}
                {formatDate(requestedEnd, tActivityCards.notAvailable)}
              </p>
            </div>
          </Alert>

          {activity.description && (
            <Alert className="h-full">
              <div className="flex items-start justify-between">
                <p className="text-sm text-neutral-800">
                  {activity.description}
                </p>
              </div>
            </Alert>
          )}

          {activity.budgetRequest?.description && (
            <Alert className="h-full">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-neutral-800 font-semibold mb-1">
                    {tActivityCards.budgetProposalCountered.originalRequestDescriptionLabel}
                  </p>
                  <p className="text-sm text-neutral-800">
                    {activity.budgetRequest.description}
                  </p>
                </div>
              </div>
            </Alert>
          )}
        </div>
      </div>
    </Card>
  );
};
